<template>
  <b-container>
    <b-row class="text-center">
      <b-col></b-col>
      <b-col v-if="internetExplorer" cols="10">
        <img src="@/assets/logo/svg/dashtech-alt-logo.svg" width="200px" class="my-3" />
        <div role="alert" aria-live="polite" aria-atomic="true" class="alert mt-3 text-center alert-danger">
          <div class="warning">
            This browser is not supported for this evaluation. <span class='d-block'>Please use a different browser, such as Chrome, Safari, Firefox, or Edge.</span>
          </div>
        </div>
      </b-col>
      <b-col v-else cols="10">
        <div v-if="logoUrl">
          <b-img 
            :src="logoUrl" 
            fluid 
            :alt="`${survey.projectName} logo`" 
            class="logo-image my-3"
          />
        </div>
        <img v-else src="@/assets/logo/svg/dashtech-alt-logo.svg" width="200px" class="my-3" />
        <BaseMessageBlock :messages="successMessage" type="Success"></BaseMessageBlock>
        <b-card
          title="Measuring Project Performance"
          style="background-color: #f2f8f8;"
          class="mb-3"
        >
          <div class="text-left">
            <p>Each month you will receive an email from DashTech reminding you to fill out the project performance evaluation.</p>
            <p>Please provide your input on each goal on a scale from 'A' to 'F', with 'A' indicating your highest level of satisfaction. Provide suggestions for improvement when you score at or below a 'B'. If the question does not apply to your role in the project, select "N/A" and continue to the next question.</p>
          </div>
        </b-card>
        <div v-if="survey.questions" class="text-left">
          <h3 class="surveyTitle mt-4 mb-3">{{survey.surveyName}} - {{invitation.surveyPeriod}}</h3>
          <form id="surveyForm" @submit.prevent="handleSubmit">
            <div v-for="question of survey.questions" v-bind:key="question.questionId">
              <b-card class="text-left mb-3">
                <b-card-title class="question__title">
                  {{question.text}} 
                  <span 
                    v-if="!isShortAnswer(question)"
                    class="text-danger"
                  >*</span>
                </b-card-title>
                <b-form-radio-group
                  v-if="requiresRadioButton(question)"
                  :id="question.questionId"
                  v-model="question.answer"
                  :options="question.answerOptions"
                  stacked
                  required
                  :name="question.questionId"
                  class="mb-3 question__radio-group"
                ></b-form-radio-group>
                <b-form-textarea
                  v-if="canShowComment(question)"
                  req
                  :id="question.questionId+'-comment'"
                  v-model="question.comment"
                  :placeholder="commentPlaceholder(question)"
                  :required="isCommentRequired(question)"
                  :rows="3"
                  :max-rows="6"
                  maxlength="1500"
                  class="question__textarea"
                ></b-form-textarea>
              </b-card>
            </div>
            <div class="text-center">
              <b-button type="submit" size="lg" variant="primary" class="mb-3">
                <b-spinner v-if="sending" label="Spinning"></b-spinner>
                <div v-else>
                  Submit
                </div>
              </b-button>
            </div>
          </form>
        </div>
        <div v-if="loading" class="text-center">
          <h5>Please wait while we load your project performance evaluation...</h5>
          <img src="/assets/loading.gif" />
        </div>
        <BaseMessageBlock :messages="uniqueErrors"></BaseMessageBlock>
        <BaseMessageBlock :messages="messages" :type="'Success'"></BaseMessageBlock>
      </b-col>
      <b-col></b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from "axios";
import authMixin from '@/mixins/auth-mixin.js';

export default {
  name: "Survey",
  mixins: [
    authMixin
  ],
  data() {
    return {
      survey: {},
      invitation: {},
      responseId: '',
      errors: [],
      messages: [],
      successMessage: [],
      loading: false,
      sending: false,
      internetExplorer: false,
      logoUrl: '',
    };
  },
  mounted() {
    this.handleIfInternetExplorer();
    this.responseId = this.$route.query.id;
    if (!this.responseId) {
      this.loading = false;
      this.errors.push({ message: "Invalid Request" });
      return;
    }
    this.checkIfCharterApproved();
    // this.loadSurvey();
  },
  methods: {
    isShortAnswer(question) {
      return question.type === 'Short Answer';
    },
    checkIfCharterApproved() {
      this.loading = true;

      // const url = `${this.$api_url}/userCharters/${this.userCharterId}/approve`;
      const url = `${this.$api_url}/userCharters/1FA1F567-9F5B-4357-8B5E-1992479EF356/approve`;

      axios
        .get(
          url,
        )
        .then((res) => {
          const isCharterApproved = (res.data === 'True');
          if (!isCharterApproved) {
            this.$router.push({ 
              name: 'CharterNeedsApproval',
              query: { 
                projectId: this.survey.projectId,
                // userCharterId: this.userCharterId,
                redirectUrl: this.$route.fullPath,
              },
            });
          } else {
            this.loadSurvey();
          }
        })
        .catch((err) => {
          this.$error({
            msg: "Couldn't check if charter approved.",
            obj: err,
          });
          this.errors.push(...this.$helpers.formatErrors(err));
          this.loading = false;
        })
        .finally(() => {
        });
    },
    loadSurvey() {
      const url = `${this.$api_url}/responses/${this.responseId}/survey`;
      
      axios
        .get(url, this.getReqConfig())
        .then(response => {
          let invitation = response.data.invitation;
          let survey = response.data.survey;
          // if (invitation.hasResponded) {
          //   this.$router.push(`/evaluation/${survey.surveyId}/results`);
          //   return;
          // } 

          this.survey = this.transformSurvey(survey);
          this.loadProjectDetails();
          this.invitation = invitation;
        })
        .catch(err => {
          console.error(err);
          this.errors.push(...this.$helpers.formatErrors(err));
          this.loading = false;
        })
        .finally(() => {
        });
    },
    loadProjectDetails() {
      const url = `${this.$api_url}/projects/${this.survey.projectId}/info`;

      axios
        .get(
          url,
        )
        .then((res) => {
          this.logoUrl = res.data.ownerLogo ? res.data.ownerLogo :
            res.data.contractorLogo ? res.data.contractorLogo :
            '';
        })
        .catch((err) => {
        })
        .finally(() => {
          this.loading = false;
        });
    },
    transformSurvey(survey) {
      let sortedSurvey = Object.assign(survey);
      sortedSurvey.questions = this.sortQuestions(sortedSurvey.questions);
      for (let q of sortedSurvey.questions) {
        q.comment = "";
        if (q.type === "Multiple Choice (Graded)") {
          q.answer = q.answerOptions[0].answerIndex;
          let hideIndex = survey.surveyType == "Facilitator Survey";
          q.answerOptions = this.sortAnswers(q.answerOptions, null, hideIndex);
        } else if (q.type == "Short Answer") {
          q.answer = null;
          q.answerOptions = this.sortAnswers(q.answerOptions, null, true);
        } else {
          q.answer = null;
          q.answerOptions = this.sortAnswers(q.answerOptions, "desc", true);
        }
      }
      return sortedSurvey;
    },
    sortQuestions(questions) {
      return questions.sort(function(a, b) {
        return a.sortOrder - b.sortOrder;
      });
    },
    sortAnswers(answerOptions, order, hideIndex) {
      if (order == "desc") {
        order = -1;
      } else {
        order = 1;
      }
      return answerOptions
        .sort(function(a, b) {
          let x = a["answerIndex"];
          let y = b["answerIndex"];
          return x < y ? (-1 * order) : x > y ? (1 * order) : 0;
        })
        .map(i => {
          let extendedAnswer = Object.assign(i);
          extendedAnswer.value = i.answerIndex;
          extendedAnswer.text =
            (i.answerIndex == "NA") || hideIndex
              ? i.answerText
              : i.answerIndex + " - " + i.answerText;
          return extendedAnswer;
        });
    },
    commentPlaceholder(question) {
      if (this.isShortAnswer(question)) {
        return "";
      }

      return this.isCommentRequired(question) ? 'Please provide feedback to proceed.' : 'We welcome your comments!'
    },
    isCommentRequired(question) {
      if (this.isShortAnswer(question)) {
        return false;
      }
      if (question.answer !== null) {
        let selectedAnswer = question.answerOptions.find(
          a => a.answerIndex == question.answer
        );
        return selectedAnswer.commentRequired;
      }
      return false;
    },
    requiresRadioButton(question) {
      return question.type == 'Safety Innovation' || 
        question.type == 'Safety Opportunity' ||
        question.type == 'Multiple Choice (Graded)';
    },
    canShowComment(question) {
      return question.type == 'Short Answer' ||
        (question.type != 'Safety Innovation' && question.type != 'Safety Opportunity') || question.answer == 'Yes';
    },
    validateAnswerOption(question) {
      return !this.isCommentRequired(question) || question.comment.trim().length > 0;
    },
    validateForm() {
      let formIsValid = true;
      for (let q of this.survey.questions) {
        if (q.type !== 'Short Answer' && q.answer === null) {
          this.errors.push({
            message: "Please select an answer for " + q.text
          });

          formIsValid &= false;
        }
        if (!this.validateAnswerOption(q)) {
          this.errors.push({
            message: "Please enter a comment for " + q.text
          });

          formIsValid &= false;
        }
      }
      return formIsValid;
    },
    handleSubmit() {
      if (!this.validateForm()) {
        this.errors.push({
          message: "Please check the page for missing or invalid answers!"
        });
        return;
      }

      // build survey response object
      let surveyResponse = {
        responseId: this.responseId,
        surveyId: this.survey.surveyId,
        projectId: this.invitation.projectId,
        answers: []
      };
      try {
        for (let q of this.survey.questions) {
          let answer = {
            questionId: q.questionId,
            questionText: q.text,
            questionType: q.type,
            questionOrder: q.sortOrder,
            commentTitle: q.commentTitle,
            answerScore: q.type === 'Short Answer' ? -2 : q.answerOptions.find(a => a.answerIndex == q.answer).answerScore,
            answerIndex: q.answer ? q.answer : '',
            answerComment: q.comment,
            answerTimestamp: new Date()
          }

          if (q.type !== "Short Answer") {
            answer.answerOptionId = q.answerOptions.find(a => a.answerIndex == q.answer).answerOptionId;
          }

          surveyResponse.answers.push(answer);
        }
      } catch (err) {
        this.errors.push(err);
        return;
      }

      this.sending = true;
      axios
        .put(`${this.$api_url}/response/${surveyResponse.responseId}`, surveyResponse, this.getReqConfig())
        .then(() => {
          if (this.survey.surveyType == "Facilitator Survey") {
            this.defaultRedirect();
            return;
          }

          if (!this.invitation.isProjectAdmin) {
            let route = { name: "Survey Results", params: { id: this.survey.surveyId } }
            if (!this.authenticated) { 
              route.query = { status: this.invitation.accountStatus, uid: this.invitation.userId }
            }
  
            this.$router.push(route);
            return;
          } 

          if (this.authenticated) {
            this.$router.push({ name: 'ProjectOverview', params: { id: this.survey.projectId }});  
            return;
          } 

          let returnUrl = `/project/${this.survey.projectId}`;
          this.$router.push({ name: 'Login', query: { message: 'Please log in to see survey results', returnUrl: returnUrl }});  
        })
        .catch(err => {
          this.errors.push(err);
          this.sending = false;
        });
    },
    handleIfInternetExplorer() {
      if (this.getBrowser() === "Internet Explorer") {
        this.internetExplorer = true;
      } else {
        this.internetExplorer = false;
      }
    },
    getBrowser() {
      try {
        // Opera 8.0+
        var isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;
        if (isOpera) {
          return "Opera";
        }
        // Firefox 1.0+
        if (typeof InstallTrigger !== 'undefined') {
          return "Firefox";
        }
        // Safari 3.0+ "[object HTMLElementConstructor]" 
        if (/constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification))) {
          return "Safari";
        }
        // Internet Explorer 6-11
        var isIE = /*@cc_on!@*/false || !!document.documentMode;
        if (isIE) {
          return "Internet Explorer";
        }
        // Edge 20+
        if (!isIE && !!window.StyleMedia) {
          return "Edge";
        }
        // Chrome 1 - 71
        var isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
        if (isChrome) {
          return "Chrome";
        }

        var isEdgeChromium = isChrome && (navigator.userAgent.indexOf("Edg") != -1);
        if (isEdgeChromium) {
          return "Edge-Chromium";
        }

        // Blink engine detection
        if ((isChrome || isOpera) && !!window.CSS) {
          return "Blink";
        }
      } catch (err) {
        return "Unknown";
      }
      return "Unknown";
    }
  },
  computed: {
    uniqueErrors() {
      return Array.from(new Set(this.errors.map(e => e.message))).map(m => ({message: m}));
    }
  }
};
</script>

<style scoped lang="scss">
.surveyTitle {
  /* color: #555; */
  font-size: 1.5em;
  text-align: center;
  color: #212529;
  line-height: 1;
}
.question__title {
  color: rgba(#212529, 0.9);
  font-size: 1.25em;
  margin-bottom: 1rem;
}
::v-deep .question__radio-group {
  .custom-control-label {
    margin-bottom: 0;
    padding-top: 0.1em;
  }
  .custom-radio {
    margin-bottom: 0.5em;
  }
}
::v-deep .question__radio-group .custom-control-label,
.question__textarea {
  font-size: 15px;
  color: #212529;
}
</style>